import { useState } from "react";
import { Input } from "@/components/ui/input";
import { ChevronUp, ChevronDown, Trash2, GripVertical, X } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import {
  FIELD_TYPE_OPTIONS,
  CHOICE_FIELD_TYPES,
  type QuestionnaireFieldDraft,
  type FieldType,
} from "./types";

// Small, clearly-bordered icon button for reorder/delete actions —
// plain ghost buttons were too low-contrast against the gray card backgrounds.
export function IconButton({
  onClick,
  disabled,
  variant = "default",
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  variant?: "default" | "danger";
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`h-7 w-7 flex items-center justify-center rounded-md border transition-colors
        disabled:opacity-30 disabled:cursor-not-allowed
        ${
          variant === "danger"
            ? "border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300"
            : "border-gray-200 text-gray-500 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-800"
        }`}
    >
      {children}
    </button>
  );
}

function OptionsEditor({
  options,
  onChange,
}: {
  options: string[];
  onChange: (opts: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  function addOption() {
    const value = draft.trim();
    if (!value) return;
    onChange([...options, value]);
    setDraft("");
  }

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-gray-600">
        Options {options.length < 2 && (
          <span className="text-amber-600 font-normal">(needs at least 2)</span>
        )}
      </label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full border border-gray-200"
          >
            {opt}
            <button
              type="button"
              onClick={() => onChange(options.filter((_, idx) => idx !== i))}
              className="text-gray-400 hover:text-red-500"
              aria-label={`Remove option ${opt}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addOption();
            }
          }}
          placeholder="Add an option…"
          className="h-8 text-sm"
        />
        <button
          type="button"
          onClick={addOption}
          className="h-8 px-3 rounded-md bg-gray-800 hover:bg-gray-900 text-white text-xs"
        >
          Add
        </button>
      </div>
    </div>
  );
}

interface QuestionnaireFieldEditorProps {
  field: QuestionnaireFieldDraft;
  onChange: (field: QuestionnaireFieldDraft) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  isFirst: boolean;
  isLast: boolean;
}

export function QuestionnaireFieldEditor({
  field,
  onChange,
  onRemove,
  onMove,
  isFirst,
  isLast,
}: QuestionnaireFieldEditorProps) {
  const isChoiceType = CHOICE_FIELD_TYPES.includes(field.field_type);
  const isCheckbox = field.field_type === "checkbox";

  return (
    <div className="border border-gray-200 rounded-md p-3 space-y-3 bg-white">
      <div className="flex items-start gap-2">
        <GripVertical size={14} className="text-gray-300 mt-2 shrink-0" />
        <div className="flex-1 space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Field type</label>
            <select
              value={field.field_type}
              onChange={(e) => {
                const field_type = e.target.value as FieldType;
                onChange({
                  ...field,
                  field_type,
                  options: CHOICE_FIELD_TYPES.includes(field_type) ? field.options : [],
                  // Clear the label when switching into/out of checkbox, since
                  // plain-text labels and rich-text consent text aren't interchangeable.
                  label: field_type === "checkbox" && !isCheckbox ? "" : field.label,
                });
              }}
              className="w-full sm:w-64 h-9 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700"
            >
              {FIELD_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {isCheckbox ? (
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">
                Checkbox text{" "}
                <span className="text-gray-400 font-normal">
                  (select text, then use the link button to add a link)
                </span>
              </label>
              <RichTextEditor
                value={field.label}
                onChange={(html) => onChange({ ...field, label: html })}
                minHeight="80px"
              />
            </div>
          ) : (
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Label</label>
              <Input
                value={field.label}
                onChange={(e) => onChange({ ...field, label: e.target.value })}
                placeholder="e.g. What's your favorite product?"
                className="h-9 text-sm"
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 mt-6 shrink-0">
          <IconButton title="Move field up" disabled={isFirst} onClick={() => onMove(-1)}>
            <ChevronUp size={14} />
          </IconButton>
          <IconButton title="Move field down" disabled={isLast} onClick={() => onMove(1)}>
            <ChevronDown size={14} />
          </IconButton>
          <IconButton title="Remove field" variant="danger" onClick={onRemove}>
            <Trash2 size={14} />
          </IconButton>
        </div>
      </div>

      {isChoiceType && (
        <OptionsEditor
          options={field.options}
          onChange={(options) => onChange({ ...field, options })}
        />
      )}

      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={field.required}
          onChange={(e) => onChange({ ...field, required: e.target.checked })}
          className="h-3.5 w-3.5 rounded border-gray-300 accent-[#00d9d9]"
        />
        <span className="text-xs text-gray-600">
          {isCheckbox ? "Must be checked to submit" : "Required"}
        </span>
      </label>
    </div>
  );
}
